import { Col, Row, Spin } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CommentsProvider } from 'contexts';
import { ShortLeft } from 'icons';
import {
  MainLayout,
  ActionHeadTitle,
  DocumentHeader,
  DocumentContent,
  DocumentComments,
  DelegateDocumentFooter
} from 'components';
import { documentsQueries, documents } from 'api';
import { useQueryParams } from 'hooks';
import { getRoute, withValues } from 'utils';
import { DocumentStatus } from 'types';

const PDF_DOCUMENT_TYPE = 'e_factura_pdf';

export const DocumentItemPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryParams = useQueryParams();
  const [isNavigating, setIsNavigating] = useState(false);

  const { data: document, isLoading } = useQuery({
    ...documentsQueries.getById(Number(id)),
    enabled: !!id
  });

  const query = useInfiniteQuery({
    ...documentsQueries.getList(
      withValues({
        ...queryParams,
        state: DocumentStatus.pendingApproval,
        type: PDF_DOCUMENT_TYPE,
        per_page: 10,
        page: queryParams.__page
      })
    ),
    getNextPageParam: (currentPage) => {
      return currentPage.next_cursor || undefined;
    },
    keepPreviousData: false,
    enabled: !!document?.id && !!document?.delegated_approvals?.length
  });

  const navigateToNextDocument = async (reset?: boolean) => {
    setIsNavigating(true);
    
    try {
      const currentDocId = document?.id;
      
      // Инвалидируем текущий документ и список документов для получения свежих данных
      await queryClient.invalidateQueries(documentsQueries.getById(Number(id)).queryKey);
      await queryClient.invalidateQueries(documentsQueries.getList().queryKey);
      
      // Небольшая задержка, чтобы сервер успел обновить статус документа
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Получаем свежий список документов, исключая текущий документ
      const freshListParams = withValues({
        ...queryParams,
        state: DocumentStatus.pendingApproval,
        type: PDF_DOCUMENT_TYPE,
        per_page: 10,
        exclude_ids: currentDocId ? [currentDocId] : undefined
      });
      
      const freshData = await documents.getList(freshListParams);
      
      // Берем первый документ из списка (уже исключен текущий через exclude_ids)
      const nextDoc = freshData.results?.[0];
      
      if (nextDoc && nextDoc.id !== currentDocId) {
        // Инвалидируем кэш следующего документа перед навигацией
        await queryClient.invalidateQueries(documentsQueries.getById(nextDoc.id).queryKey);
        
        navigate(
          getRoute('PartnerDocumentItemPage', {
            query: {
              ...queryParams
            },
            id: nextDoc.id
          }),
          { state: { lastDocumentId: currentDocId }, replace: true }
        );
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const handleApproveSuccess = () => {
    navigateToNextDocument(true);
  };

  return (
    <MainLayout
      pageHeading={document?.title && <ActionHeadTitle Icon={ShortLeft} label={document.title} />}
    >
      {document && (
        <Spin spinning={isLoading || query.isFetchingNextPage || isNavigating}>
          <DocumentHeader document={document} />
          <CommentsProvider>
            <Row align="top" justify="space-between">
              <Col flex={1}>
                <DocumentContent document={document} />
              </Col>
              <Col>
                <DocumentComments document={document} />
              </Col>
            </Row>
          </CommentsProvider>

          {!!document?.delegated_approvals?.length && (
            <DelegateDocumentFooter
              delegatedDocument={document.delegated_approvals}
              onApproveSuccess={handleApproveSuccess}
            />
          )}
        </Spin>
      )}
    </MainLayout>
  );
};
